import { useEffect, useRef, useState, useContext } from 'preact/hooks';
import { Field, useField } from 'formik';
import { FormField } from './FormField';
import { FieldError } from './FieldError';
import { tryParseDecimal } from '../utils.common/tryParseDecimal';
import { ECardGlobalState } from './ECardGlobalState';

export default function ECardStepDonationSelect(props) {
    const { hidden, donationOptions, donationBands, recipients } = props;

    if (donationOptions == null) {
        return null;
    }

    const { selectedRecipient, isScheduled, isGtmCard } = useContext(ECardGlobalState);
    const [deliveryDateField, deliveryDateMeta, deliveryDateHelpers] = useField('DeliveryDate');
    const [donationAmount, donationAmountMeta, donationAmountHelpers] = useField('EcardMessageDetails.DonationAmount');
    const [otherDonationAmount, otherDonationAmountMeta, otherDonationAmountHelpers] = useField('OtherDonationAmount');
    const [isOtherAmount, isOtherAmountMeta, isOtherAmountHelpers] = useField('IsOtherAmount');
    const [skipDonation, skipDonationMeta, skipDonationHelpers] = useField('SkipDonation');

    const initialActiveOptionIndex = donationOptions.findIndex((o) => o.Amount >= 1 && o.Amount === donationAmount.value);

    const [showOtherInput, setShowOtherInput] = useState(isOtherAmount.value === true && initialActiveOptionIndex === -1 && otherDonationAmount.value !== '');
    const [activeOption, setActiveOption] = useState(showOtherInput === true ? 3 : initialActiveOptionIndex);

    const inputRef = useRef(null);

    const handleOtherAmountOnChange = (event) => {
        otherDonationAmountHelpers.setValue(event.target.value, true);
    };

    useEffect(() => {
        if (showOtherInput) {
            isOtherAmountHelpers.setValue(true);
            inputRef.current?.focus();
        } else {
            if (otherDonationAmount.value !== otherDonationAmountMeta.initialValue) {
                otherDonationAmountHelpers.setValue(otherDonationAmountMeta.initialValue);
            }
            isOtherAmountHelpers.setValue(false);
            otherDonationAmountHelpers.setValue('');
        }
    }, [showOtherInput]);
    
    useEffect(() => {
        if (otherDonationAmount.value !== '') {
            donationAmountHelpers.setValue(tryParseDecimal(otherDonationAmount.value, 0));
        }
    }, [otherDonationAmount.value]);
    
    const summaryMessage = `You are sending ${recipients.length} card${recipients.length > 1 ? "s" : ""} ${isScheduled.get ? `and they will be scheduled to arrive on ${new Date(deliveryDateMeta.value).toLocaleDateString("en-GB", { day: 'numeric', month: 'numeric', year: 'numeric' })}.` : "to be sent out immediately."} We'd suggest the following donation amounts:`;
    const displayedDonationOptions = donationBands.filter(band => band.Minimum <= recipients.length && recipients.length < band.Maximum).length > 0 
                                    ? donationBands.filter(band => band.Minimum <= recipients.length && recipients.length < band.Maximum)[0].DonationOptions 
                                    : donationOptions;
    
                                    const donationOptionsEls = displayedDonationOptions.map((option, index) => {
        return (
            <div class="donation-option" key={index}>
                <div class="donation-option__button-wrapper">
                    <button
                        class={`donation-option__button button-reset ${activeOption === index ? ' donation-option__button--active' : ''}`}
                        type="button"
                        onClick={() => {
                            skipDonationHelpers.setValue(false);

                            if (activeOption === index) {
                                setActiveOption(null);
                                setShowOtherInput(false);
                                donationAmountHelpers.setValue(0);
                            } else {
                                setActiveOption(index);

                                if (option.Amount < 0) {
                                    setShowOtherInput(true);

                                    if (otherDonationAmount.value === undefined) {
                                        donationAmountHelpers.setValue(0);
                                    } else {
                                        donationAmountHelpers.setValue(otherDonationAmount.value);
                                    }
                                } else {
                                    setShowOtherInput(false);
                                    donationAmountHelpers.setValue(option.Amount);
                                }
                            }
                        }}
                    >
                        {option.Label}
                        {option.IconUrl && <img src={option.IconUrl} alt="" class="donation-option__button-icon" />}
                    </button>
                    {option.Amount < 0 && (
                        <>
                            <label class="sr-only" htmlFor={`${otherDonationAmount.name}-${index}`}>
                                Enter other amount
                            </label>
                            <div class={`donation-option__other ${!showOtherInput ? 'donation-option__other--hidden' : ''}`}>
                                &pound;
                                <input
                                    ref={inputRef}
                                    class="input donation-option__other-input"
                                    type="number"
                                    min="1"
                                    inputmode="decimal"
                                    value={otherDonationAmount.value}
                                    id={`${otherDonationAmount.name}-${index}`}
                                    disabled={!showOtherInput}
                                    onChange={handleOtherAmountOnChange}
                                    onBlur={() => {
                                        if (otherDonationAmount.value !== otherDonationAmountMeta.initialValue) {
                                            otherDonationAmountHelpers.setTouched(true);
                                        }
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
                {showOtherInput && option.Amount < 0 && <FieldError {...otherDonationAmountMeta} class="donation-option__other-error" />}
                {option.Description && <p class="donation-option__description">{option.Description}</p>}
            </div>
        );
    });

    return (
        <section class="ecard-form__section ecard-form__section--donation" hidden={hidden} aria-labelledby="donation-step-head">
            <header class="ecard-form__header">
                <p class="ecard-form__step-count">Step 3 of 4</p>
                <h1 class="ecard-form__title text-form-h2 text-blue" id="donation-step-head">
                    Would you like to include a donation to Marie Curie?
                </h1>
                {isGtmCard === false && <p class="ecard-form__caption">{summaryMessage}</p>}
            </header>

            <div class="double-col-grid double-col-grid--fixed">{donationOptionsEls}</div>

            <FieldError
                error={donationAmountMeta.error}
                touched={skipDonation.value === false && isOtherAmount.value === false && donationAmountMeta.touched}
            />

            <FormField
                type="checkbox"
                wrapperClass="checked-item--small-label"
                name="EcardMessageDetails.DonationVisible"
                label="Display this donation on the card"
            />
        </section>
    );
}
